import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import BalloonDrop from "./BalloonDrop";
import { BALLOONS } from "../../lib/media";
import BookingDialog from "./BookingDialog";
import { useLang } from "../../lib/i18n";
import HeroIntroLoop from "./HeroIntroLoop";

function Balloon({
  src,
  alt,
  top,
  left,
  right,
  bottom,
  size = 200,
  rotate = 0,
  scrollSpeed = -60,
  drift = { y: 18, x: 8, r: 2, dur: 10 },
  delay = 0,
  layer = 20,
  scrollYProgress,
}) {
  const yScroll = useTransform(scrollYProgress, [0, 1], [0, scrollSpeed]);
  return (
    <motion.div
      style={{ y: yScroll, top, left, right, bottom, width: size, height: size, zIndex: layer }}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, delay, ease: [0.16, 1, 0.3, 1] }}
      className="absolute pointer-events-none"
    >
      <motion.div
        animate={{
          y: [0, -drift.y, 0, drift.y * 0.6, 0],
          x: [0, drift.x, 0, -drift.x * 0.7, 0],
          rotate: [rotate - drift.r, rotate + drift.r, rotate - drift.r * 0.5, rotate + drift.r * 0.8, rotate - drift.r],
        }}
        transition={{ duration: drift.dur, repeat: Infinity, ease: "easeInOut", delay }}
        className="w-full h-full"
      >
        <img src={src} alt={alt} className="w-full h-full object-contain balloon select-none" draggable={false} />
      </motion.div>
    </motion.div>
  );
}

// Butterfly — transform-based animation (no layout-triggering left/top), keeps
// the GPU happy. Path values stay in viewport-percent units (vw / svh) so the
// butterflies still travel across the full hero on every screen size.
function Butterfly({ src, size = 90, duration = 32, delay = 0, path, flipped = false }) {
  const xVals = path.map((p) => `${parseFloat(p.x)}vw`);
  const yVals = path.map((p) => `${parseFloat(p.y)}svh`);
  const rotVals = path.map((p) => p.r ?? 0);
  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ x: xVals, y: yVals, rotate: rotVals }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
        times: xVals.map((_, i) => i / (xVals.length - 1)),
      }}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: size,
        height: size,
        zIndex: 15,
        opacity: 1,
        willChange: "transform",
      }}
      className="pointer-events-none"
    >
      <motion.div
        animate={{ scaleX: [1, 0.9, 1, 0.92, 1], scaleY: [1, 1.02, 1, 1.03, 1] }}
        transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
        className="w-full h-full"
        style={{ transform: flipped ? "scaleX(-1)" : undefined, willChange: "transform" }}
      >
        <img src={src} alt="Butterfly" className="butterfly-img w-full h-full object-contain balloon select-none" draggable={false} />
      </motion.div>
    </motion.div>
  );
}

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yHead = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const [bookOpen, setBookOpen] = useState(false);

  // Celebration interaction: idle → drop (instant)
  const [stage, setStage] = useState("idle");

  const onMedalClick = () => {
    if (stage === "idle") setStage("drop");
  };

  // Dynamically measure the fixed header height so the medal stays flush
  // with the bottom of the header on every screen size (mobile / tablet
  // landscape / desktop). Updates on viewport resize.
  const [headerH, setHeaderH] = useState(80);
  useEffect(() => {
    const header = document.querySelector('[data-testid="site-header"]');
    if (!header) return;
    const measure = () => {
      const h = header.getBoundingClientRect().height;
      if (h > 0) setHeaderH(h);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(header);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const pathA = [
    { x: "-8%", y: "60%", r: 10 },
    { x: "14%", y: "44%", r: -6 },
    { x: "34%", y: "32%", r: 8 },
    { x: "54%", y: "38%", r: -10 },
    { x: "74%", y: "28%", r: 6 },
    { x: "94%", y: "50%", r: -4 },
    { x: "104%", y: "42%", r: 8 },
    { x: "70%", y: "74%", r: -8 },
    { x: "30%", y: "80%", r: 6 },
    { x: "-8%", y: "60%", r: 10 },
  ];
  const pathB = [
    { x: "104%", y: "26%", r: -10 },
    { x: "82%", y: "40%", r: 8 },
    { x: "58%", y: "34%", r: -6 },
    { x: "36%", y: "48%", r: 10 },
    { x: "16%", y: "66%", r: -6 },
    { x: "-8%", y: "58%", r: 4 },
    { x: "22%", y: "22%", r: -10 },
    { x: "60%", y: "16%", r: 8 },
    { x: "92%", y: "30%", r: -6 },
    { x: "104%", y: "26%", r: -10 },
  ];

  return (
    <>
      <section
        id="home"
        ref={ref}
        data-testid="hero-section"
        style={{ position: "relative" }}
        className="relative min-h-[100svh] w-full overflow-hidden bg-white flex items-center justify-center pt-40 sm:pt-48 pb-24 sm:pb-32"
      >
        {/* Background video + intro text loop (DO → IT → WITH → LOVE) */}
        <HeroIntroLoop />

        {/* Cinematic vignette + barely-visible grain — premium edge feel */}
        <div aria-hidden className="hero-vignette absolute inset-0 z-[1] pointer-events-none" />
        <div aria-hidden className="hero-grain absolute inset-0 z-[2] pointer-events-none" />

        {/* Light overlay — kept very minimal so video stays clearly visible while
            the colourful retro headline (with strong black stroke) remains readable. */}
        <div
          aria-hidden
          data-testid="hero-overlay"
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.10) 35%, rgba(255,255,255,0.18) 100%)",
          }}
        />

        {/* CTA + decoration block — brand elements (medal, logo, slogan,
            hashtags, BOOK button, scroll indicator) sit at z-[40], ABOVE
            the intro overlay (z-30), so they always remain visible while
            the intro words animate. Headline and butterflies stay below
            the overlay so they're hidden during intro. */}

        {/* TOP-LEFT: gold anniversary medal hanging directly under the header.
            Clickable — triggers the gold/silver confetti + balloon celebration. */}
        <motion.div
          data-testid="hero-medal"
          role="button"
          tabIndex={0}
          aria-label="Trigger 10 year anniversary celebration"
          onClick={onMedalClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onMedalClick();
            }
          }}
          initial={{ opacity: 0, y: -16 }}
          animate={{
            opacity: 1,
            y: 0,
            // Subtle "wiggle to invite click" — tiny rotation pulse every ~5s.
            rotate: [-1.6, 1.6, -0.8, 1.2, -1.6, 1.6, -0.8, 0],
          }}
          transition={{
            opacity: { duration: 1.0, delay: 0.1, ease: [0.16, 1, 0.3, 1] },
            y:       { duration: 1.0, delay: 0.1, ease: [0.16, 1, 0.3, 1] },
            rotate:  {
              duration: 5,
              repeat: Infinity,
              repeatDelay: 1.6,
              ease: "easeInOut",
              times: [0, 0.05, 0.10, 0.15, 0.20, 0.25, 0.30, 1],
            },
          }}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          className="medal-shimmer z-[40] cursor-pointer select-none"
          style={{
            position: "absolute",
            top: `${headerH}px`,
            left: "3.5%",
            width: "min(13vw, 152px)",
            minWidth: "70px",
            transformOrigin: "50% 0%",
            WebkitMaskImage: `url(${BALLOONS.goldMedal})`,
            maskImage: `url(${BALLOONS.goldMedal})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
          }}
        >
          <motion.img
            src={BALLOONS.goldMedal}
            alt="10 Years Anniversary medal"
            draggable={false}
            className="balloon w-full h-auto object-contain block pointer-events-none"
          />
        </motion.div>

        {/* TOP-RIGHT brand block removed — the header now carries the logo
            + supplier tagline. Hero stays clean over the video on mobile. */}

        {/* "DO IT WITH LOVE" headline removed — the intro overlay is the
            only place that text appears. Once the video plays, it stays
            clean with only the permanent brand elements visible. */}

        {/* BOOK YOUR SHOOT button + bottom-left language/socials cluster
            both removed — order flow lives in the header, the booking flow
            is reachable through the navigation. Hero stays clean over the
            video. */}

        {/* BOTTOM-CENTER: animated scroll-mouse indicator.
            A static parent handles true viewport-relative centering with
            `left: 0; right: 0; flex justify-center`, so the inner motion.div
            can animate y/opacity without ever fighting the centering
            transform (the bug we saw with `-translate-x-1/2`). */}
        <div
          aria-hidden
          className="absolute z-[40] left-0 right-0 flex justify-center pointer-events-none"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + clamp(1.5rem, 6svh, 3rem))" }}
        >
          <motion.div
            data-testid="hero-scroll-indicator"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center gap-2"
          >
            <div className="scroll-mouse">
              <span className="scroll-mouse-dot" />
            </div>
            <span
              className="font-body text-[9px] uppercase font-semibold text-black/70"
              style={{ letterSpacing: "0.28em" }}
            >
              Scroll
            </span>
          </motion.div>
        </div>

        {/* FRONT LAYER — butterflies, always fully visible, no fade */}
        <Butterfly src={BALLOONS.butterflyPink} size={96} duration={36} delay={0.9} path={pathA} />
        <Butterfly src={BALLOONS.butterflyBlue} size={84} duration={40} delay={2.4} path={pathB} flipped />
      </section>

      <BookingDialog open={bookOpen} onOpenChange={setBookOpen} mode="book" />

      {/* Balloon + confetti drop */}
      <AnimatePresence>
        {stage === "drop" && (
          <BalloonDrop onDone={() => setStage("idle")} duration={10000} />
        )}
      </AnimatePresence>
    </>
  );
}
